import * as React from 'react';
import moment from 'moment-timezone';
import {
    DetailsList,
    getTheme,
    SelectionMode,
    IGroup,
    ScrollablePane,
    DetailsListLayoutMode,
    Sticky,
    StickyPositionType,
    List,
    PrimaryButton,
} from '@fluentui/react';
import { CheckboxIcon, CheckboxCompositeIcon } from '@fluentui/react-icons';
import { getFocusStyle, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Kunde, useKundeList } from 'api/kunde';
import { AngebotPosition } from 'api/angebotPosition';
import { AuftragPosition } from 'api/auftragPosition';
import { RechnungPosition } from 'api/rechnungPosition';
import { Ansprechpartner } from 'api/ansprechpartner';
import { Maschinenkunde } from 'api/maschinenkunde';
import { Termin } from 'api/termin';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { ErrorIndicator } from 'layout/ErrorIndicator/ErrorIndicator';
import { useBetreuerByNr, useBetreuerByKz } from 'api/betreuer';
import classnames from 'classnames';
import AutoSizer from 'react-virtualized-auto-sizer';
import { createReportUrl, formatDecimalTime, formatMoney, formatSmDate } from 'utils/formatter';
import usePersistentState from 'hooks/usePersistentState';
import { useSelector } from 'react-redux';
import { AuftragStatusLabel, KundeArtLabel, RechnungStatusLabel, TerminArtLabel } from 'utils/labels';
import { formStyles } from 'styles/formStyles';
import {
    artikelGruppenByNrSelector,
    branchenByNrSelector,
    einheitenByCodeSelector,
    erweiterteSuchenByNrSelector,
    interesseFuerProdukteByNrSelector,
} from 'store/lookups/selector';
import { TerminEdit, TerminEditData } from 'components/Termin/TerminEdit';
import nl2br from 'react-nl2br';
import { AngebotEdit, AngebotEditData } from 'components/Angebot/AngebotEdit';
import { Helmet } from 'react-helmet';
import { generateReportPrintUrl } from 'reports/utils';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { authSelector } from 'store/auth/selector';

const theme: ITheme = getTheme();
const { palette, semanticColors, fonts } = theme;

const styles = mergeStyleSets({
    container: {
        position: 'relative',
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 5,
        height: 'calc(100vh - 75px)',
    },
    printButton: {
        position: 'absolute',
        right: 0,
        top: 10,
    },
    blocks: {
        overflowX: 'hidden',
        overflowY: 'scroll',
    },
    box: {
        boxShadow: '0px 6px 13px -7px #000000, 0px 0px 1px 0px rgba(0,0,0,0)',
        border: '1px solid #CCC',
        margin: '10px 5px',
    },
    archiv: {
        backgroundColor: '#F7BDBC',
    },
    boxHeader: {
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        fontSize: 16,
        marginLeft: 6,
        marginTop: 6,
        marginBottom: 6,
        outline: 'none',
    },
    scrollablePane: {
        top: 30,
    },
    addToTask: {
        position: 'absolute',
        top: 0,
        right: 5,
    },
    content: {
        position: 'relative',
    },
    line: {
        marginTop: 5,
        marginLeft: 11,
    },
    label: {
        width: 130,
        display: 'inline-block',
    },
    value: {
        display: 'inline-block',
        fontWeight: 700,
    },
    leftDetails: {
        marginTop: 10,
        display: 'inline-block',
        verticalAlign: 'top',
        width: '50%',
    },
    rightDetails: {
        marginTop: 10,
        display: 'inline-block',
        verticalAlign: 'top',
        width: '50%',
    },
    note: {
        margin: 11,
    },
    defaultCell: {
        fontSize: theme.fonts.medium.fontSize,
    },
    numericCell: {
        fontSize: theme.fonts.medium.fontSize,
        textAlign: 'right',
    },
    terminItemCell: [
        getFocusStyle(theme, { inset: -1 }),
        {
            minHeight: 54,
            padding: 10,
            boxSizing: 'border-box',
            borderBottom: `1px solid ${semanticColors.bodyDivider}`,
            cursor: 'pointer',
            selectors: {
                '&:hover': { background: palette.neutralLight },
            },
        },
    ],
    terminStatus: {
        position: 'absolute',
        right: 12,
    },
    terminKontakt: {
        fontWeight: 700,
        width: '50%',
        display: 'inline-block',
    },
    terminZeit: {
        fontWeight: 700,
        width: '50%',
        display: 'inline-block',
    },
    terminBetreff: {
        fontWeight: 700,
        marginTop: 5,
    },
    terminText: {
        fontSize: fonts.medium.fontSize,
    },
    valueList: {
        display: 'grid',
        gridTemplateColumns: '130px 1fr',
    },
    umsaetze: {
        display: 'grid',
        gridTemplateColumns: '130px repeat(4, 1fr)',
    },
    berichte: {
        display: 'grid',
        gridTemplateColumns: '130px repeat(4, max-content)',
        gridColumnGap: 15,
    },
    createTaskNotiz: {
        // height: 240,
    },
});

const detailsListStyles = {
    headerWrapper: {
        selectors: {
            '.ms-DetailsHeader': {
                // paddingTop: 0,
            },
        },
    },
};

interface KundeOverviewProps {
    kundeNr?: number;
}

export const KundeOverview: React.FC<KundeOverviewProps> = ({ kundeNr }) => {
    const auth = useSelector(authSelector);
    const artikelGruppeByNr = useSelector(artikelGruppenByNrSelector);

    const kundeList = useKundeList({
        nr: kundeNr,
        includeArchiv: true,
        withTaskBetreuerNrList: true,
        withAnsprechpartnerList: true,
        withMaschinenkundeList: true,
        withAngebotList: true,
        withAngebotPositionList: true,
        withAuftragList: true,
        withAuftragPositionList: true,
        withRechnungList: true,
        withRechnungPositionList: true,
        withTerminList: true,
        withBrancheList: true,
        withProduktgruppeList: false,
        withErweiterteSucheList: true,
        withInteresseFuerProdukteList: true,
        withMahnstufen: true,
    });

    const [kunde, setKunde] = React.useState<Kunde | null>();

    const [ansprechpartnerOpen, setAnsprechpartnerOpen] = usePersistentState(true, 'KundeDetail.ansprechpartnerOpen');
    const [maschinenKundeOpen, setMaschinenKundeOpen] = usePersistentState(true, 'KundeDetail.maschinenKundeOpen');
    const [angebotOpen, setAngebotOpen] = usePersistentState(true, 'KundeDetail.angebotOpen');
    const [auftragOpen, setAuftragOpen] = usePersistentState(true, 'KundeDetail.auftragOpen');
    const [rechnungOpen, setRechnungOpen] = usePersistentState(true, 'KundeDetail.rechnungOpen');
    const [terminOpen, setTerminOpen] = usePersistentState(true, 'KundeDetail.terminOpen');

    React.useEffect(() => {
        if (!kundeList.isLoading && !kundeList.isError && kundeList.data && kundeList.data.length > 0) {
            setKunde(kundeList.data[0]);
        } else {
            setKunde(null);
        }
    }, [kundeList]);

    const betreuerByNr = useBetreuerByNr();
    const branchenByNr = useSelector(branchenByNrSelector);
    const interesseFuerProdukteByNr = useSelector(interesseFuerProdukteByNrSelector);
    const erweiterteSuchenByNr = useSelector(erweiterteSuchenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);
    const [terminEditData, setTerminEditData] = React.useState<TerminEditData | null>(null);
    const [angebotEditData, setAngebotEditData] = React.useState<AngebotEditData | null>(null);

    const generateLabelValueLine = (
        label: string,
        value: string | number | null | undefined,
        render?: (value: string | number) => JSX.Element,
    ): JSX.Element | null => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        return (
            <div className={styles.line}>
                <div className={styles.label}>{label}</div>
                <div className={styles.value}>{render ? render(value) : value}</div>
            </div>
        );
    };

    const renderAnsprechpartner = () => {
        if (!kunde || !kunde.ansprechpartnerList || kunde.ansprechpartnerList.length === 0) {
            return null;
        }
        return (
            <div className={styles.box}>
                <button className={styles.boxHeader} onClick={() => setAnsprechpartnerOpen(!ansprechpartnerOpen)}>
                    Ansprechpartner {ansprechpartnerOpen ? '' : '...'}
                </button>
                {ansprechpartnerOpen && (
                    <DetailsList
                        items={kunde.ansprechpartnerList}
                        selectionMode={SelectionMode.none}
                        compact
                        styles={detailsListStyles}
                        columns={[
                            {
                                key: 'name',
                                name: 'Ansprechpartner',
                                fieldName: 'name',
                                minWidth: 150,
                                maxWidth: 150,
                                isResizable: true,
                                className: styles.defaultCell,
                                onRender: (item: Ansprechpartner) => (item.titel ? item.titel + ' ' : '') + item.name,
                            },
                            {
                                key: 'position',
                                name: 'Funktion',
                                fieldName: 'position',
                                minWidth: 150,
                                maxWidth: 150,
                                isResizable: true,
                                className: styles.defaultCell,
                            },
                            {
                                key: 'telefon',
                                name: 'Telefon',
                                fieldName: 'telefon',
                                minWidth: 200,
                                maxWidth: 150,
                                isResizable: true,
                                className: styles.defaultCell,
                                onRender: (item: Ansprechpartner) =>
                                    item.telefon && <a href={'tel:' + item.telefon}>{item.telefon}</a>,
                            },
                            {
                                key: 'mobil',
                                name: 'Mobil',
                                fieldName: 'mobil',
                                minWidth: 200,
                                maxWidth: 150,
                                isResizable: true,
                                className: styles.defaultCell,
                                onRender: (item: Ansprechpartner) =>
                                    item.mobil && <a href={'tel:' + item.mobil}>{item.mobil}</a>,
                            },
                            {
                                key: 'mail',
                                name: 'Mail',
                                fieldName: 'mail',
                                minWidth: 0,
                                maxWidth: 220,
                                isResizable: true,
                                className: styles.defaultCell,
                                onRender: (item: Ansprechpartner) => {
                                    return item.mail && <a href={'mailto:' + item.mail}>{item.mail}</a>;
                                },
                            },
                        ]}
                    />
                )}
            </div>
        );
    };

    const renderMaschinenkunde = () => {
        if (!kunde || !kunde.maschinenkundeList || kunde.maschinenkundeList.length === 0) {
            return null;
        }

        return (
            <div className={styles.box}>
                <button className={styles.boxHeader} onClick={() => setMaschinenKundeOpen(!maschinenKundeOpen)}>
                    Maschinenkunde {maschinenKundeOpen ? '' : '...'}
                </button>
                {maschinenKundeOpen && (
                    <DetailsList
                        items={kunde.maschinenkundeList}
                        selectionMode={SelectionMode.none}
                        compact
                        styles={detailsListStyles}
                        columns={[
                            {
                                key: 'name',
                                name: 'Hersteller',
                                fieldName: 'hersteller',
                                minWidth: 150,
                                maxWidth: 150,
                                isResizable: true,
                                className: styles.defaultCell,
                            },
                            {
                                key: 'type',
                                name: 'Maschine',
                                fieldName: 'type',
                                minWidth: 150,
                                maxWidth: 150,
                                isResizable: true,
                                className: styles.defaultCell,
                            },
                            {
                                key: 'baujahr',
                                name: 'Baujahr',
                                fieldName: 'baujahr',
                                minWidth: 50,
                                maxWidth: 50,
                                isResizable: true,
                                className: styles.defaultCell,
                                onRender: (item: Maschinenkunde) => {
                                    return item.baujahr || '';
                                },
                            },
                            {
                                key: 'geraeteNr',
                                name: 'Geräte-Nr.',
                                fieldName: 'geraeteNr',
                                minWidth: 50,
                                maxWidth: 150,
                                isResizable: true,
                                className: styles.defaultCell,
                            },
                            {
                                key: 'lDat',
                                name: 'Lieferdatum',
                                fieldName: 'type',
                                minWidth: 50,
                                maxWidth: 150,
                                isResizable: true,
                                className: styles.defaultCell,
                                onRender: (item: Maschinenkunde) => {
                                    return formatSmDate(item.lDat, true);
                                },
                            },
                            {
                                key: 'datAusgesch',
                                name: 'Ausgeschieden seit',
                                fieldName: 'type',
                                minWidth: 50,
                                maxWidth: 150,
                                isResizable: true,
                                className: styles.defaultCell,
                                onRender: (item: Maschinenkunde) => {
                                    return formatSmDate(item.datAusgesch, true);
                                },
                            },
                            {
                                key: 'bem',
                                name: 'Bemerkung',
                                fieldName: 'bem',
                                minWidth: 0,
                                maxWidth: 220,
                                isResizable: true,
                                className: styles.defaultCell,
                            },
                        ]}
                    />
                )}
            </div>
        );
    };

    const angebotGroups = React.useMemo<IGroup[]>(() => {
        const groups: IGroup[] = [];

        if (
            !kunde?.angebotList ||
            kunde?.angebotList.length === 0 ||
            !kunde?.angebotPositionList ||
            kunde?.angebotPositionList.length === 0
        ) {
            return [];
        }

        kunde.angebotList.forEach((angebot) => {
            const startIndex = (kunde?.angebotPositionList || []).findIndex((pos) => pos.aNr === angebot.nr);

            let count = 0;
            if (startIndex >= 0) {
                do {
                    count += 1;
                } while ((kunde?.angebotPositionList || [])[startIndex + count]?.aNr === angebot.nr);
            }

            groups.push({
                key: angebot.nr,
                name:
                    angebot.nr +
                    '   (' +
                    moment(angebot.datum).format('DD.MM.YYYY') +
                    '), Netto: ' +
                    formatMoney(angebot.sNetto),
                startIndex,
                count,
            });
        });
        return groups;
    }, [kunde]);

    const auftagGroups = React.useMemo<IGroup[]>(() => {
        const groups: IGroup[] = [];

        if (
            !kunde?.auftragList ||
            kunde?.auftragList.length === 0 ||
            !kunde?.auftragPositionList ||
            kunde?.auftragPositionList.length === 0
        ) {
            return [];
        }

        kunde.auftragList.forEach((auftrag) => {
            const startIndex = (kunde?.auftragPositionList || []).findIndex((pos) => pos.auNr === auftrag.nr);

            let count = 0;
            if (startIndex >= 0) {
                do {
                    count += 1;
                } while ((kunde?.auftragPositionList || [])[startIndex + count]?.auNr === auftrag.nr);
            }

            groups.push({
                key: auftrag.nr,
                name:
                    auftrag.nr +
                    '   (' +
                    moment(auftrag.datum).format('DD.MM.YYYY') +
                    '), Netto: ' +
                    formatMoney(auftrag.sNetto) +
                    ', Fracht Lager: ' +
                    formatMoney(auftrag.fracht) +
                    ', Tipp-Prov: ' +
                    formatMoney(auftrag.tipp) +
                    ', Status: ' +
                    (AuftragStatusLabel[auftrag.status] || auftrag.status),
                startIndex,
                count,
            });
        });
        return groups;
    }, [kunde]);

    const rechnungGroups = React.useMemo<IGroup[]>(() => {
        const groups: IGroup[] = [];

        if (
            !kunde?.rechnungList ||
            kunde?.rechnungList.length === 0 ||
            !kunde?.rechnungPositionList ||
            kunde?.rechnungPositionList.length === 0
        ) {
            return [];
        }

        kunde.rechnungList.forEach((rechnung) => {
            const startIndex = (kunde?.rechnungPositionList || []).findIndex((pos) => pos.rNr === rechnung.nr);

            let count = 0;
            if (startIndex >= 0) {
                do {
                    count += 1;
                } while ((kunde?.rechnungPositionList || [])[startIndex + count]?.rNr === rechnung.nr);
            }

            groups.push({
                key: rechnung.nr,
                name:
                    rechnung.nr +
                    '   (' +
                    moment(rechnung.datum).format('DD.MM.YYYY') +
                    '), Netto: ' +
                    formatMoney(rechnung.sNetto) +
                    ', Fracht Lager: ' +
                    formatMoney(rechnung.fracht) +
                    ', Tipp-Prov: ' +
                    formatMoney(rechnung.tipp) +
                    ', Status: ' +
                    (RechnungStatusLabel[rechnung.status] || rechnung.status),
                startIndex,
                count,
                data: rechnung,
            });
        });
        return groups;
    }, [kunde]);

    const renderAngebot = () => {
        if (angebotGroups.length === 0) {
            return null;
        }

        return (
            <div
                className={styles.box}
                style={{
                    height: angebotOpen ? 400 : 20,
                    position: 'relative',
                    maxHeight: 'inherit',
                    paddingRight: 15,
                    paddingBottom: 15,
                }}
            >
                <button className={styles.boxHeader} onClick={() => setAngebotOpen(!angebotOpen)}>
                    Angebote {angebotOpen ? '' : '...'}
                </button>
                {angebotOpen && (
                    <ScrollablePane className={styles.scrollablePane}>
                        <DetailsList
                            groups={angebotGroups}
                            items={kunde?.angebotPositionList || []}
                            selectionMode={SelectionMode.none}
                            groupProps={{
                                showEmptyGroups: true,
                                onRenderHeader: (props, defaultRender): JSX.Element => {
                                    const divProps = {
                                        onClick: () => {
                                            if (props?.groupIndex !== undefined && props?.groups) {
                                                const group = props.groups[props?.groupIndex];
                                                setAngebotEditData({
                                                    kNr: kundeNr,
                                                    aNr: group.key,
                                                });
                                            }
                                        },
                                    };
                                    return <div {...divProps}>{defaultRender && defaultRender(props)}</div>;
                                },
                            }}
                            compact
                            styles={detailsListStyles}
                            layoutMode={DetailsListLayoutMode.fixedColumns}
                            onRenderDetailsHeader={(detailsHeaderProps, defaultRender) => (
                                <Sticky stickyPosition={StickyPositionType.Header}>
                                    {defaultRender
                                        ? defaultRender({
                                              ...detailsHeaderProps,
                                          } as never)
                                        : null}
                                </Sticky>
                            )}
                            columns={[
                                {
                                    key: 'artikelGruppeNr',
                                    name: 'Artikel Gruppe',
                                    fieldName: 'artikelGruppeNr',
                                    minWidth: 70,
                                    maxWidth: 200,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                    onRender: (item: AngebotPosition) => {
                                        return item.artikelGruppeNr
                                            ? artikelGruppeByNr[item.artikelGruppeNr].text || item.artikelGruppeNr
                                            : '';
                                    },
                                },
                                {
                                    key: 'artikelNr',
                                    name: 'Artikel Nr.',
                                    fieldName: 'artikelNr',
                                    minWidth: 60,
                                    maxWidth: 200,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                },
                                {
                                    key: 'posText',
                                    name: 'Angebot',
                                    fieldName: 'posText',
                                    minWidth: 300,
                                    maxWidth: 500,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                },
                                {
                                    key: 'posEinh',
                                    name: 'Einheit',
                                    fieldName: 'posEinh',
                                    minWidth: 50,
                                    maxWidth: 50,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                    onRender: (item: AngebotPosition) => {
                                        return item.posEinh ? einheitenByCode[item.posEinh].text || item.posEinh : '?';
                                    },
                                },
                                {
                                    key: 'posMenge',
                                    name: 'Menge',
                                    fieldName: 'posMenge',
                                    minWidth: 50,
                                    maxWidth: 50,
                                    isResizable: true,
                                    className: styles.numericCell,
                                    onRender: (item: AngebotPosition) => {
                                        return item.posMenge ? item.posMenge.toFixed(2) : '?';
                                    },
                                },
                                {
                                    key: 'posEkPreis',
                                    name: 'EK Preis',
                                    fieldName: 'posEkPreis',
                                    minWidth: 100,
                                    maxWidth: 100,
                                    isResizable: true,
                                    className: styles.numericCell,
                                    onRender: (item: AngebotPosition) => {
                                        return item.posEkPreis ? formatMoney(item.posEkPreis) : '-';
                                    },
                                },
                                {
                                    key: 'posEPreis',
                                    name: 'Preis/Einh.',
                                    fieldName: 'posEPreis',
                                    minWidth: 100,
                                    maxWidth: 100,
                                    isResizable: true,
                                    className: styles.numericCell,
                                    onRender: (item: AngebotPosition) => {
                                        return item.posEPreis ? formatMoney(item.posEPreis) : '-';
                                    },
                                },
                                {
                                    key: 'posNetto',
                                    name: 'Preis (Netto)',
                                    fieldName: 'posNetto',
                                    minWidth: 100,
                                    maxWidth: 100,
                                    isResizable: true,
                                    className: styles.numericCell,
                                    onRender: (item: AngebotPosition) => {
                                        return item.posNetto ? formatMoney(item.posNetto) : '-';
                                    },
                                },
                            ]}
                        />
                    </ScrollablePane>
                )}
            </div>
        );
    };

    const renderAuftrag = () => {
        if (auftagGroups.length === 0) {
            return null;
        }

        return (
            <div
                className={styles.box}
                style={{
                    height: auftragOpen ? 400 : 20,
                    position: 'relative',
                    maxHeight: 'inherit',
                    paddingRight: 15,
                    paddingBottom: 15,
                }}
            >
                <button className={styles.boxHeader} onClick={() => setAuftragOpen(!auftragOpen)}>
                    Aufträge {auftragOpen ? '' : '...'}
                </button>
                {auftragOpen && (
                    <ScrollablePane className={styles.scrollablePane}>
                        <DetailsList
                            groups={auftagGroups}
                            items={kunde?.auftragPositionList || []}
                            selectionMode={SelectionMode.none}
                            groupProps={{
                                showEmptyGroups: true,
                            }}
                            compact
                            styles={detailsListStyles}
                            layoutMode={DetailsListLayoutMode.fixedColumns}
                            onRenderDetailsHeader={(detailsHeaderProps, defaultRender) => (
                                <Sticky stickyPosition={StickyPositionType.Header}>
                                    {defaultRender
                                        ? defaultRender({
                                              ...detailsHeaderProps,
                                          } as never)
                                        : null}
                                </Sticky>
                            )}
                            columns={[
                                {
                                    key: 'artikelGruppeNr',
                                    name: 'Artikel Gruppe',
                                    fieldName: 'artikelGruppeNr',
                                    minWidth: 70,
                                    maxWidth: 200,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                    onRender: (item: AuftragPosition) => {
                                        return item.artikelGruppeNr
                                            ? artikelGruppeByNr[item.artikelGruppeNr].text || item.artikelGruppeNr
                                            : '';
                                    },
                                },
                                {
                                    key: 'artikelNr',
                                    name: 'Artikel Nr.',
                                    fieldName: 'artikelNr',
                                    minWidth: 60,
                                    maxWidth: 200,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                },
                                {
                                    key: 'posText',
                                    name: 'Auftrag',
                                    fieldName: 'posText',
                                    minWidth: 300,
                                    maxWidth: 500,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                },
                                {
                                    key: 'posEinh',
                                    name: 'Einheit',
                                    fieldName: 'posEinh',
                                    minWidth: 50,
                                    maxWidth: 50,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                    onRender: (item: AuftragPosition) => {
                                        return item.posEinh ? einheitenByCode[item.posEinh].text || item.posEinh : '?';
                                    },
                                },
                                {
                                    key: 'posMenge',
                                    name: 'Menge',
                                    fieldName: 'posMenge',
                                    minWidth: 50,
                                    maxWidth: 50,
                                    isResizable: true,
                                    className: styles.numericCell,
                                    onRender: (item: AuftragPosition) => {
                                        return item.posMenge ? item.posMenge.toFixed(2) : '?';
                                    },
                                },
                                {
                                    key: 'posEkPreis',
                                    name: 'EK Preis',
                                    fieldName: 'posEkPreis',
                                    minWidth: 100,
                                    maxWidth: 100,
                                    isResizable: true,
                                    className: styles.numericCell,
                                    onRender: (item: AuftragPosition) => {
                                        return item.posEkPreis ? formatMoney(item.posEkPreis) : '-';
                                    },
                                },
                                {
                                    key: 'posEPreis',
                                    name: 'Preis/Einh.',
                                    fieldName: 'posEPreis',
                                    minWidth: 100,
                                    maxWidth: 100,
                                    isResizable: true,
                                    className: styles.numericCell,
                                    onRender: (item: AuftragPosition) => {
                                        return item.posEPreis ? formatMoney(item.posEPreis) : '-';
                                    },
                                },
                                {
                                    key: 'posNetto',
                                    name: 'Preis (Netto)',
                                    fieldName: 'posNetto',
                                    minWidth: 100,
                                    maxWidth: 100,
                                    isResizable: true,
                                    className: styles.numericCell,
                                    onRender: (item: AuftragPosition) => {
                                        return item.posNetto ? formatMoney(item.posNetto) : '-';
                                    },
                                },
                            ]}
                        />
                    </ScrollablePane>
                )}
            </div>
        );
    };

    const renderRechnung = () => {
        if (rechnungGroups.length === 0) {
            return null;
        }

        return (
            <div
                className={styles.box}
                style={{
                    height: rechnungOpen ? 400 : 20,
                    position: 'relative',
                    maxHeight: 'inherit',
                    paddingRight: 15,
                    paddingBottom: 15,
                }}
            >
                <button className={styles.boxHeader} onClick={() => setRechnungOpen(!rechnungOpen)}>
                    Rechnungen {rechnungOpen ? '' : '...'}
                </button>
                {rechnungOpen && (
                    <ScrollablePane className={styles.scrollablePane}>
                        <DetailsList
                            groups={rechnungGroups}
                            items={kunde?.rechnungPositionList || []}
                            selectionMode={SelectionMode.none}
                            groupProps={{
                                showEmptyGroups: true,
                                onRenderHeader: (headerProps, defaultRender) => {
                                    return defaultRender
                                        ? defaultRender({
                                              ...headerProps,
                                              className: headerProps?.group?.data?.status !== 6 && formStyles.important,
                                          } as never)
                                        : null;
                                },
                            }}
                            compact
                            styles={detailsListStyles}
                            layoutMode={DetailsListLayoutMode.fixedColumns}
                            onRenderDetailsHeader={(detailsHeaderProps, defaultRender) => (
                                <Sticky stickyPosition={StickyPositionType.Header}>
                                    {defaultRender
                                        ? defaultRender({
                                              ...detailsHeaderProps,
                                          } as never)
                                        : null}
                                </Sticky>
                            )}
                            columns={[
                                {
                                    key: 'artikelGruppeNr',
                                    name: 'Artikel Gruppe',
                                    fieldName: 'artikelGruppeNr',
                                    minWidth: 70,
                                    maxWidth: 200,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                    onRender: (item: AuftragPosition) => {
                                        return item.artikelGruppeNr
                                            ? artikelGruppeByNr[item.artikelGruppeNr].text || item.artikelGruppeNr
                                            : '';
                                    },
                                },
                                {
                                    key: 'artikelNr',
                                    name: 'Artikel Nr.',
                                    fieldName: 'artikelNr',
                                    minWidth: 60,
                                    maxWidth: 200,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                },
                                {
                                    key: 'posText',
                                    name: 'Rechnung',
                                    fieldName: 'posText',
                                    minWidth: 300,
                                    maxWidth: 500,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                },
                                {
                                    key: 'posEinh',
                                    name: 'Einheit',
                                    fieldName: 'posEinh',
                                    minWidth: 50,
                                    maxWidth: 50,
                                    isResizable: true,
                                    className: styles.defaultCell,
                                    onRender: (item: RechnungPosition) => {
                                        return item.posEinh ? einheitenByCode[item.posEinh].text || item.posEinh : '?';
                                    },
                                },
                                {
                                    key: 'posMenge',
                                    name: 'Menge',
                                    fieldName: 'posMenge',
                                    minWidth: 50,
                                    maxWidth: 50,
                                    isResizable: true,
                                    className: styles.numericCell,
                                    onRender: (item: RechnungPosition) => {
                                        return item.posMenge ? item.posMenge.toFixed(2) : '?';
                                    },
                                },
                                {
                                    key: 'posNetto',
                                    name: 'Preis (Netto)',
                                    fieldName: 'posNetto',
                                    minWidth: 100,
                                    maxWidth: 100,
                                    isResizable: true,
                                    className: styles.numericCell,
                                    onRender: (item: RechnungPosition) => {
                                        return item.posNetto ? formatMoney(item.posNetto) : '-';
                                    },
                                },
                            ]}
                        />
                    </ScrollablePane>
                )}
            </div>
        );
    };

    let websiteUrl = '';
    if (kunde?.www) {
        websiteUrl =
            kunde.www.startsWith('http://') || kunde.www.startsWith('https://') ? kunde.www : 'http://' + kunde.www;
    }

    const renderHead = () => {
        if (!kunde) {
            return null;
        }

        const thisYear = parseInt(moment().format('YYYY'), 10);

        return (
            <div className={classnames(styles.box, { [styles.archiv]: kunde.archiv })} style={{ paddingBottom: 10 }}>
                <div className={styles.leftDetails}>
                    {kunde.archiv && (
                        <div className={styles.line}>
                            <div className={styles.label}>
                                <strong>Archiviert!</strong>
                            </div>
                        </div>
                    )}
                    {generateLabelValueLine('K-Art: ', kunde.art ? KundeArtLabel[kunde.art] || kunde.art : '')}
                    {generateLabelValueLine('K-Nr: ', kunde.nr)}
                    {kunde.konzernNr
                        ? generateLabelValueLine('Konzern: ', kunde.konzernName1 + ' (' + kunde.konzernNr + ')')
                        : ''}
                    {generateLabelValueLine('Betreuer: ', betreuerByNr[kunde.betreuer]?.name || kunde.betreuer)}
                    {generateLabelValueLine(
                        'Tel-Betreuer: ',
                        betreuerByNr[kunde.telBetreuer]?.name || kunde.telBetreuer,
                    )}
                </div>
                <div className={styles.rightDetails}>
                    {generateLabelValueLine('Telefon: ', kunde.telefon, (value) => (
                        <a href={('tel: ' + value) as string}>{value}</a>
                    ))}
                    {generateLabelValueLine('Fax: ', kunde.fax)}
                    {generateLabelValueLine('Mail: ', kunde.mail, (value) => (
                        <a href={'mailto: ' + value}>{value}</a>
                    ))}
                    {generateLabelValueLine('Webseite: ', kunde.www, (value) => (
                        <a target="_blank" rel="noreferrer" href={websiteUrl}>
                            {value}
                        </a>
                    ))}
                </div>
                <div className={classnames(styles.line, styles.valueList)}>
                    <div>Branchen: </div>
                    <div>{(kunde?.brancheList || []).map((nr) => branchenByNr[nr]?.text || nr).join(', ')}</div>
                </div>
                <div className={classnames(styles.line, styles.valueList)}>
                    <div>Interesse für Produkte: </div>
                    <div>
                        {(kunde?.interesseFuerProdukteList || [])
                            .map((nr) => interesseFuerProdukteByNr[nr]?.text || nr)
                            .join(', ')}
                    </div>
                </div>
                <div className={classnames(styles.line, styles.valueList)}>
                    <div>Erweiterte Suche: </div>
                    <div>
                        {(kunde?.erweiterteSucheList || [])
                            .map((nr) => erweiterteSuchenByNr[nr]?.text || nr)
                            .join(', ')}
                    </div>
                </div>
                <div className={classnames(styles.line, styles.umsaetze)}>
                    <div>Umsätze: </div>
                    {kunde?.umsatzLfd ? (
                        <div>
                            <strong>{formatMoney(kunde?.umsatzLfd)}</strong> ({thisYear})
                        </div>
                    ) : null}
                    {kunde?.umsatzVj1 ? (
                        <div>
                            <strong>{formatMoney(kunde?.umsatzVj1)}</strong> ({thisYear - 1})
                        </div>
                    ) : null}
                    {kunde?.umsatzVj2 ? (
                        <div>
                            <strong>{formatMoney(kunde?.umsatzVj2)}</strong> ({thisYear - 2})
                        </div>
                    ) : null}
                    {kunde?.umsatzVj3 ? (
                        <div>
                            <strong>{formatMoney(kunde?.umsatzVj3)}</strong> ({thisYear - 3})
                        </div>
                    ) : null}
                </div>
                <div className={classnames(styles.line, styles.umsaetze)}>
                    <div>Marge (Brutto): </div>
                    {kunde?.bMargeLfd ? (
                        <div>
                            <strong>{formatMoney(kunde?.bMargeLfd)}</strong> ({thisYear})
                        </div>
                    ) : null}
                    {kunde?.bMargeVj1 ? (
                        <div>
                            <strong>{formatMoney(kunde?.bMargeVj1)}</strong> ({thisYear - 1})
                        </div>
                    ) : null}
                    {kunde?.bMargeVj2 ? (
                        <div>
                            <strong>{formatMoney(kunde?.bMargeVj2)}</strong> ({thisYear - 2})
                        </div>
                    ) : null}
                    {kunde?.bMargeVj3 ? (
                        <div>
                            <strong>{formatMoney(kunde?.bMargeVj3)}</strong> ({thisYear - 3})
                        </div>
                    ) : null}
                </div>
                {(auth.rolle === 'GF' || auth.rolle === 'ID') && (
                    <div className={classnames(styles.line, styles.umsaetze)}>
                        <div>Offene Rechnungen: </div>
                        {kunde?.mahnstufe0 ? (
                            <div>
                                <strong>{formatMoney(kunde?.mahnstufe0)}</strong> (Mahnstufe 0)
                            </div>
                        ) : null}
                        {kunde?.mahnstufe1 ? (
                            <div>
                                <strong>{formatMoney(kunde?.mahnstufe1)}</strong> (Mahnstufe 1)
                            </div>
                        ) : null}
                        {kunde?.mahnstufe2 ? (
                            <div>
                                <strong>{formatMoney(kunde?.mahnstufe2)}</strong> (Mahnstufe 2)
                            </div>
                        ) : null}
                        {kunde?.mahnstufe3 ? (
                            <div>
                                <strong>{formatMoney(kunde?.mahnstufe3)}</strong> (Mahnstufe 3)
                            </div>
                        ) : null}
                    </div>
                )}
                {(auth.rolle === 'GF' || auth.rolle === 'ID') && (
                    <div className={classnames(styles.line, styles.berichte)}>
                        <div>Berichte: </div>
                        <OpenLink link={createReportUrl('angebotsuebersicht', kunde?.nr)} label="Angebotsübersicht" />
                        <OpenLink link={createReportUrl('auftragsuebersicht', kunde?.nr)} label="Auftragsübersicht" />
                        <OpenLink link={createReportUrl('rechnungsuebersicht', kunde?.nr)} label="Rechnungsübersicht" />
                        <OpenLink link={createReportUrl('umsatzuebersicht', kunde?.nr)} label="Umsatzübersicht" />
                    </div>
                )}
                {kunde.bemerkung && <div className={styles.note}>{kunde.bemerkung}</div>}
            </div>
        );
    };

    const betreuerByShort = useBetreuerByKz();

    const onRenderTerminCell = (termin?: Termin): JSX.Element => {
        return (
            <div
                className={classnames(styles.terminItemCell, { [formStyles.important]: termin?.prodInfo })}
                onClick={() => {
                    if (termin?.kNr && termin?.zfKaNr) {
                        setTerminEditData({
                            kNr: termin.kNr,
                            zfKaNr: termin.zfKaNr,
                        });
                    }
                }}
            >
                <div className={styles.terminStatus}>
                    {termin?.terminStatus ? <CheckboxCompositeIcon /> : <CheckboxIcon />}
                </div>
                <div className={styles.terminKontakt}>
                    {termin?.userKz &&
                        (betreuerByShort[termin?.userKz] ? betreuerByShort[termin?.userKz]?.name : termin?.userKz)}
                    {' - '}
                    {termin?.kaName || '?'}
                </div>
                <div className={styles.terminZeit}>
                    {termin?.datumVon && moment(termin?.datumVon).format('DD.MM.YYYY')}{' '}
                    {formatDecimalTime(termin?.zeitVon)}
                    {' - '}
                    {formatDecimalTime(termin?.zeitBis)}{' '}
                    {termin?.terminArt && (TerminArtLabel[termin.terminArt] || termin.terminArt)}
                </div>
                <div className={styles.terminBetreff}>{termin?.terminBetreff}</div>
                <div className={styles.terminText}>{nl2br(termin?.terminText)}</div>
                <div>{termin?.terminStatus}</div>
            </div>
        );
    };

    const renderTermin = () => {
        if (!kunde || !kunde.terminList || kunde.terminList.length === 0) {
            return null;
        }

        return (
            <div className={styles.box}>
                <button className={styles.boxHeader} onClick={() => setTerminOpen(!terminOpen)}>
                    Kontakthistorie {terminOpen ? '' : '...'}
                </button>
                {terminOpen && (
                    <div style={{ width: '100%', maxHeight: 500, overflowX: 'hidden', overflowY: 'scroll' }}>
                        <List items={kunde.terminList || []} onRenderCell={onRenderTerminCell} />
                    </div>
                )}
            </div>
        );
    };

    if (!kundeNr) {
        return null;
    }

    return (
        <div className={styles.container}>
            <Helmet>
                <title>{'Kunde ' + kundeNr + ' | Übersicht'}</title>
            </Helmet>
            <PrimaryButton
                className={styles.printButton}
                onClick={() => {
                    window.open(
                        generateReportPrintUrl('kunde', {
                            kNr: kundeNr,
                        }),
                    );
                }}
                text="Drucken"
            />
            {kundeList.isLoading && <LoadingIndicator />}
            {kundeList.isError && <ErrorIndicator error={kundeList.error} />}
            {kunde && (
                <>
                    <div>
                        <div className={formStyles.topTitle}>{`${kunde.name1} ${kunde.name2}`}</div>
                        <div className={styles.line}>
                            {kunde.plz} {kunde.ort}, {kunde.strasse} {kunde.land && `(${kunde.land})`}
                        </div>
                    </div>
                    <div className={styles.content}>
                        <AutoSizer>
                            {({ height, width }) => (
                                <div
                                    style={{ width, height, overflowX: 'hidden', overflowY: 'scroll' }}
                                    className={styles.blocks}
                                >
                                    <div>
                                        {renderHead()}
                                        {renderAnsprechpartner()}
                                        {renderMaschinenkunde()}
                                        {renderAngebot()}
                                        {renderAuftrag()}
                                        {renderRechnung()}
                                        {renderTermin()}
                                    </div>
                                </div>
                            )}
                        </AutoSizer>
                    </div>
                </>
            )}
            {terminEditData !== null && (
                <TerminEdit
                    defaultKNr={terminEditData.kNr}
                    zfKaNr={terminEditData.zfKaNr}
                    onClose={(refetch) => {
                        setTerminEditData(null);
                        if (refetch) {
                            kundeList.refetch();
                        }
                    }}
                />
            )}
            {angebotEditData !== null && (
                <AngebotEdit
                    defaultKNr={angebotEditData.kNr}
                    aNr={angebotEditData.aNr}
                    onClose={(refetch) => {
                        setAngebotEditData(null);
                        if (refetch) {
                            kundeList.refetch().then();
                        }
                    }}
                    onReopen={(newANr: string) => {
                        setAngebotEditData(null);
                        kundeList.refetch().then(() => {
                            setAngebotEditData({
                                kNr: angebotEditData.kNr,
                                aNr: newANr,
                            });
                        });
                    }}
                />
            )}
        </div>
    );
};
